import * as React from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { getPartnerListForm } from "../../lib/apis/dtos/request/partner.request";
import type { PartnerListItem } from "../../lib/apis/dtos/response/partner.response";
import { usePartnerList } from "../../lib/apis/hooks/partner.hook";
import { partnerPageUrl } from "../../lib/apis/dtos/request/partner.request";
import Button from "../../components/ui/button";
import { useModalStore } from "../../store/useModalStore";
import { MODAL_KEY } from "../../lib/constants/modal.constants";

const DEFAULT_PAGE_SIZE = 10;

// PDF 1장 컬럼 순서 그대로
const HEADERS = ["제휴 업체명", "제휴 유형", "제휴기간", "활성상태", "시세표 게시", "시세표 편집", "상세보기", "연결링크"];

/** 2026-09-10 → 26.09.10 */
const shortDate = (d: string | null) => (d ? d.slice(2).replace(/-/g, ".") : "");
const formatPeriod = (from: string | null, to: string | null) =>
  !from && !to ? "-" : `${shortDate(from)}~${shortDate(to)}`;

interface PartnerRowProps {
  partner: PartnerListItem;
  onDetail: () => void;
  onEditQuotation: () => void;
}

function PartnerRow({ partner, onDetail, onEditQuotation }: PartnerRowProps) {
  const { partnerName, activeFrom, activeTo, isActive, quotationSubmit, link, partnerType, partnerTypeName } = partner;
  return (
    <TableRow>
      <TableCell>{partnerName || "-"}</TableCell>
      <TableCell>{partnerTypeName || "-"}</TableCell>
      <TableCell>{formatPeriod(activeFrom, activeTo)}</TableCell>
      <TableCell>
        <Button
          variant={isActive ? "primary" : "gray"}
          label={isActive ? "활성" : "비활성"}
          className="h-6 px-[10px] rounded-[100px] text-[13px] font-normal"
        />
      </TableCell>
      <TableCell>
        {/* TODO(backend): 목록 응답에 게시 시각(recentSubmitted/isAuto)이 오면 "HH:mm:ss (자동)" 으로 표시 */}
        <span className={quotationSubmit ? "text-[#2E7D32]" : "text-black/40"}>{quotationSubmit ? "게시 완료" : "미게시"}</span>
      </TableCell>
      <TableCell>
        <Button variant="outline" label="제출" className="h-8 px-4 text-[13px]" onClick={onEditQuotation} />
      </TableCell>
      <TableCell>
        <Button variant="outline" label="상세보기" className="h-8 px-3 text-[13px]" onClick={onDetail} />
      </TableCell>
      <TableCell>
        {link ? (
          <a href={partnerPageUrl(link, partnerType)} target="_blank" rel="noreferrer" className="text-[#1976D2] underline break-all">
            {link}
          </a>
        ) : (
          "-"
        )}
      </TableCell>
    </TableRow>
  );
}

export default function PartnerPage() {
  const navigate = useNavigate();
  const { openModal } = useModalStore();

  const [form, setForm] = React.useState<getPartnerListForm>({ page: 0, size: DEFAULT_PAGE_SIZE });

  const { data, isSuccess } = useQuery({ ...usePartnerList(form) });
  const content = data?.result?.content || [];
  const totalElements = data?.result?.totalElements || 0;

  return (
    <div className="p-6">
      <div className="flex-row-between px-[16px] mb-[16px]">
        <p className="text-[14px] font-medium">전체 {totalElements}개</p>
        <Button
          label="업체 추가하기"
          className="bg-[#ffffff] text-[14px] w-[120px] h-[36px] text-[#1976D2] rounded-[3px] border border-[#1976D280]"
          onClick={() => openModal(MODAL_KEY.PartnerCreate, {})}
        />
      </div>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {HEADERS.map((label) => (
                <TableCell key={label}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isSuccess &&
              content.map((partner) => (
                <PartnerRow
                  key={partner.partnerId}
                  partner={partner}
                  onDetail={() => navigate(`/partner/${partner.partnerId}`)}
                  onEditQuotation={() => navigate(`/partner/quotation/${partner.partnerId}`)}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={totalElements}
        rowsPerPage={form.size}
        page={form.page}
        onPageChange={(_, newPage) => setForm((prev) => ({ ...prev, page: newPage }))}
        onRowsPerPageChange={(e) => setForm({ page: 0, size: parseInt(e.target.value, 10) })}
      />
    </div>
  );
}
